import { ApiError, api, type TeamTextView } from '../api/client';
import { onAppCall } from './calls';
import { myChoiceNow, type LiveEvent } from './messages';
import { patchBusy } from './patch';
import { DEFAULT_TEAM_SOUND, nextReminderAt, reminderTurn, spokenLine, teamAlertPlan, type QuietCheck, type TeamOfAlert, type TeamSoundKind, type TeamSoundPrefs, type TeamVolume } from './teamSound';

/**
 * Plays what lib/teamSound decides for a text to your ring group or queue (1.12.33): the team text
 * sound, the spoken line, and the reminders while it waits. Made with Web Audio and the browser's
 * own voice (speechSynthesis): no sound files. Like the message sound, it can only play once the
 * person has clicked or typed in Stitch (browsers' rule); it's ready from that first time.
 */

// --- the sounds ------------------------------------------------------------------------------------

/**
 * A counter bell (a customer at the desk): two bright strikes. Urgent: three quicker, louder ones.
 * Unlike the message sound (two quick drops that slide down), the Patch chime (soft rising notes)
 * and every wallboard sound (steady tones). On a call: one soft strike, at a third of the volume.
 */
const BELL_HZ = 1480;
/** A struck bell's partials: how far above the note, how loud, how long they ring (seconds). */
const PARTIALS: [number, number, number][] = [
  [1, 1, 1.1],
  [2.76, 0.42, 0.55],
  [5.4, 0.2, 0.3]
];
const STRIKES: Record<TeamSoundKind, { t: number; g: number }[]> = {
  team: [
    { t: 0, g: 0.8 },
    { t: 0.3, g: 0.7 }
  ],
  urgent: [
    { t: 0, g: 1 },
    { t: 0.17, g: 0.95 },
    { t: 0.34, g: 1 }
  ],
  soft: [{ t: 0, g: 0.3 }]
};
const LEVEL: Record<TeamVolume, number> = { low: 0.16, medium: 0.38, high: 0.75 };
/** How long a sound rings before the spoken line starts. */
const SAY_AFTER_MS: Record<TeamSoundKind, number> = { team: 900, urgent: 1000, soft: 0 };

type AudioCtor = typeof AudioContext;
let ctx: AudioContext | null = null;
function context(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext) as AudioCtor | undefined;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
}

let armed = false;
/**
 * Browsers start sound (and speech) only after the person clicks or types in the page: be ready
 * from the first time, as the message sound is (lib/alerts).
 */
export function armTeamSound(): void {
  if (armed || typeof window === 'undefined') return;
  armed = true;
  const wake = () => {
    const c = context();
    if (c && c.state !== 'running') void c.resume().catch(() => undefined);
    // Some browsers (Safari) only speak later if they've spoken once from a click: a silent word now.
    if (!spokeOnce && typeof speechSynthesis !== 'undefined' && typeof SpeechSynthesisUtterance !== 'undefined') {
      spokeOnce = true;
      try {
        const u = new SpeechSynthesisUtterance(' ');
        u.volume = 0;
        speechSynthesis.speak(u);
      } catch {
        // No voice here: the sound still plays.
      }
    }
  };
  for (const e of ['pointerdown', 'keydown', 'touchend'] as const) window.addEventListener(e, wake, { passive: true });
}
let spokeOnce = false;

function bellStrike(c: AudioContext, out: AudioNode, at: number, gain: number): number {
  let end = at;
  for (const [mult, level, secs] of PARTIALS) {
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(gain * level, at + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, at + secs);
    g.connect(out);
    const o = c.createOscillator();
    o.type = 'sine';
    o.frequency.value = BELL_HZ * mult;
    o.connect(g);
    o.start(at);
    o.stop(at + secs + 0.02);
    end = Math.max(end, at + secs);
  }
  return end;
}

/** Plays one of the sounds. False when the browser won't yet (no click in the page so far). */
function playBell(kind: TeamSoundKind, volume: TeamVolume): boolean {
  const c = context();
  if (!c || c.state !== 'running') return false;
  const master = c.createGain();
  master.gain.value = LEVEL[volume];
  master.connect(c.destination);
  const start = c.currentTime + 0.02;
  let end = start;
  for (const s of STRIKES[kind]) end = Math.max(end, bellStrike(c, master, start + s.t, s.g));
  window.setTimeout(() => master.disconnect(), Math.ceil((end - c.currentTime + 0.2) * 1000));
  return true;
}

// --- the spoken line ---------------------------------------------------------------------------------

const VOICE: Record<TeamVolume, number> = { low: 0.45, medium: 0.8, high: 1 };

/** Says the line in the browser's own voice. False when it can't (no voices here, or not allowed yet). */
function say(words: string, volume: TeamVolume): boolean {
  if (typeof speechSynthesis === 'undefined' || typeof SpeechSynthesisUtterance === 'undefined') return false;
  try {
    const u = new SpeechSynthesisUtterance(words);
    u.lang = 'en-US';
    u.rate = 1;
    u.volume = VOICE[volume];
    speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
}

// --- a record of what played, for Stitch's own end-to-end tests -----------------------------------------

/** What was played (and said), when a test asks: `window.__stitchSoundLog = []` before it happens. */
export interface PlayedSound {
  sound: TeamSoundKind | 'message';
  words: string | null;
  reminder: boolean;
  conversationId: string;
  at: number;
}
function record(p: Omit<PlayedSound, 'at'>): void {
  const log = typeof window !== 'undefined' ? (window as unknown as { __stitchSoundLog?: unknown }).__stitchSoundLog : undefined;
  if (Array.isArray(log)) log.push({ ...p, at: Date.now() });
}

// --- their choices (Your settings → Team texts) ---------------------------------------------------------

let prefs: TeamSoundPrefs = DEFAULT_TEAM_SOUND;
let me = '';
/** Their choices, as signed in (Shell keeps this up to date): switched off, any reminders stop. */
export function setTeamSoundPrefs(next: TeamSoundPrefs, userId?: string): void {
  prefs = next;
  if (userId !== undefined) me = userId;
  if (!next.sound || !next.reminders) stopReminders();
}

/** From a click (Your settings → "Play"): the sound once, and the spoken line when it's on. */
export async function previewTeamSound(choice: TeamSoundPrefs, kind: TeamSoundKind = 'team'): Promise<void> {
  armTeamSound();
  const c = context();
  if (c && c.state !== 'running') await c.resume().catch(() => undefined);
  playBell(kind, choice.volume);
  if (choice.speak && kind !== 'soft') window.setTimeout(() => say(spokenLine({ name: 'your team', urgent: kind === 'urgent' }), choice.volume), SAY_AFTER_MS[kind]);
}

// --- what's going on here -------------------------------------------------------------------------------

/** What lib/alerts knows about the page: whether Stitch is in front on that conversation, quiet hours, the message sound. */
export interface AlertEnv {
  /** Stitch is in front, on this conversation. */
  reading(conversationId: string): boolean;
  /** Their quiet hours now, while Stitch isn't in front (the same rule as the page's notifications). */
  quietHours(): Promise<boolean>;
  /** The ordinary message sound (their device's own choice applies). True when it played. */
  messageSound(): boolean;
}

/**
 * Their own do not disturb and calls, per company, as the live stream last said (presence events
 * about them): on a call anywhere (a desk phone too), for the reminders after the alert.
 */
const myState = new Map<string, { dnd?: boolean; talking?: boolean }>();
export function teamPresence(e: LiveEvent): void {
  if (e.type !== 'presence' || !e.tenant || !me || e.userId !== me) return;
  const t = e.tenant.toLowerCase();
  const now = { ...myState.get(t) };
  if (e.me) now.dnd = e.me.choice === 'dnd';
  if (e.presence) {
    now.dnd = e.presence.status === 'dnd';
    now.talking = e.presence.phone?.state === 'talking';
  }
  myState.set(t, now);
}

function quietCheck(tenant: string, conversationId: string, serverOnCall: boolean, env: AlertEnv, quietHours: boolean): QuietCheck {
  const heard = myState.get(tenant.toLowerCase());
  return {
    dnd: myChoiceNow(tenant) === 'dnd' || heard?.dnd === true,
    onCall: serverOnCall || heard?.talking === true || onAppCall() || patchBusy(),
    reading: env.reading(conversationId),
    quietHours
  };
}

// --- the alert ----------------------------------------------------------------------------------------

/**
 * A team text's alert in the Stitch that's open (one tab: lib/alerts has claimed it): the sound
 * (or the soft tone, on a call), the spoken line after it, and the reminders. True when a sound played.
 */
export async function teamTextAlert(e: LiveEvent, env: AlertEnv, inFront: boolean): Promise<boolean> {
  const t = e.team;
  if (!t || !e.conversationId || !e.tenant) return false;
  const team: TeamOfAlert = { extension: t.extension, name: t.name, urgent: t.urgent };
  // The server's word on a call (wherever it is) holds until the stream says it ended.
  if (t.onCall) myState.set(e.tenant.toLowerCase(), { ...myState.get(e.tenant.toLowerCase()), talking: true });
  const quiet = inFront ? false : await env.quietHours();
  const plan = teamAlertPlan(prefs, team, quietCheck(e.tenant, e.conversationId, t.onCall, env, quiet));
  let played = false;
  if (plan.sound === 'message') {
    played = env.messageSound();
    record({ sound: 'message', words: null, reminder: false, conversationId: e.conversationId });
  } else if (plan.sound) {
    played = playBell(plan.sound, prefs.volume);
    record({ sound: plan.sound, words: plan.speak, reminder: false, conversationId: e.conversationId });
    if (plan.speak) {
      const words = plan.speak;
      const volume = prefs.volume;
      window.setTimeout(() => say(words, volume), SAY_AFTER_MS[plan.sound]);
    }
  }
  // A newer alert for the same conversation starts its reminders afresh.
  if (plan.remind) startReminders(e.tenant, e.conversationId, team, env);
  else stopReminders(e.conversationId);
  return played;
}

// --- reminders while it waits ----------------------------------------------------------------------------

interface Running {
  tenant: string;
  conversationId: string;
  team: TeamOfAlert;
  startedAt: number;
  turns: number;
  timer: number | undefined;
  env: AlertEnv;
}
const running = new Map<string, Running>();

function startReminders(tenant: string, conversationId: string, team: TeamOfAlert, env: AlertEnv): void {
  stopReminders(conversationId);
  const r: Running = { tenant, conversationId, team, startedAt: Date.now(), turns: 0, timer: undefined, env };
  running.set(conversationId, r);
  schedule(r);
}

function schedule(r: Running): void {
  const at = nextReminderAt(r);
  if (at === null) {
    running.delete(r.conversationId);
    return;
  }
  r.timer = window.setTimeout(() => void turn(r), Math.max(0, at - Date.now()));
}

/** Where things stand with the conversation now (null: it's gone, or no longer theirs to see). */
async function viewOf(r: Running): Promise<TeamTextView | null | 'unknown'> {
  try {
    const d = await api.conversation(r.tenant, r.conversationId);
    return d.conversation.team ?? null;
  } catch (err) {
    // Not found (moved away from them): stop. Anything else (a dropped connection): try next time.
    return err instanceof ApiError && (err.status === 404 || err.status === 403) ? null : 'unknown';
  }
}

async function turn(r: Running): Promise<void> {
  if (running.get(r.conversationId) !== r) return;
  const view = await viewOf(r);
  if (running.get(r.conversationId) !== r) return;
  let step: 'play' | 'skip' | 'stop';
  if (view === 'unknown') step = 'skip';
  else {
    const quiet = document.visibilityState === 'visible' && document.hasFocus() ? false : await r.env.quietHours();
    // On a call: as the live stream last said (a desk phone too), or this device's calls and Patches.
    step = reminderTurn(r, prefs, view, r.team, quietCheck(r.tenant, r.conversationId, false, r.env, quiet));
  }
  if (step === 'stop') {
    stopReminders(r.conversationId);
    return;
  }
  if (step === 'play') {
    const kind: TeamSoundKind = r.team.urgent || (view !== 'unknown' && view?.urgent) ? 'urgent' : 'team';
    playBell(kind, prefs.volume);
    record({ sound: kind, words: null, reminder: true, conversationId: r.conversationId });
  }
  r.turns += 1;
  schedule(r);
}

/** Stops the reminders for a conversation (or all of them). */
export function stopReminders(conversationId?: string): void {
  for (const [id, r] of [...running]) {
    if (conversationId && id !== conversationId) continue;
    window.clearTimeout(r.timer);
    running.delete(id);
  }
}

/**
 * Something changed in a conversation with reminders going (taken, answered, resolved): looked at
 * straight away, so they stop as soon as it's no longer theirs, not at the next turn.
 */
export async function teamConversationChanged(conversationId: string): Promise<void> {
  const r = running.get(conversationId);
  if (!r) return;
  const view = await viewOf(r);
  if (view === 'unknown' || running.get(conversationId) !== r) return;
  if (reminderTurn({ ...r, turns: 0 }, prefs, view, r.team, { dnd: false, onCall: false, reading: false, quietHours: false }) === 'stop') stopReminders(conversationId);
}

/** The reminders going now (for Stitch's own tests). */
export function remindersRunning(): string[] {
  return [...running.keys()];
}

if (typeof window !== 'undefined') {
  // Signed out: nothing more plays.
  window.addEventListener('stitch:signed-out', () => stopReminders());
}
